import { defineStore } from 'pinia';
import { ref, watch, nextTick } from 'vue';
import { getDraftEdits, saveDraftEdits, type DraftEdit } from '../api/draft';

export type TriageStatus = 'proposed' | 'confirmed' | 'rejected' | 'unassigned' | 'needs-input';

export interface TriageDraft {
  eventId: string;
  status: TriageStatus;
  wbs1?: string;
  wbs1Name?: string;
  wbs2?: string;
  wbs2Name?: string;
  wbs3?: string;
  wbs3Name?: string;
  hours?: number;
  comment?: string;
  commentKind?: 'agent' | 'user' | 'edited';
  laborCode?: string;
  dayIndex?: number;
  signatureHash?: string;
  category?: string;
  // See DraftEdit.useAnyway — overrules the in-VP shape match for this event.
  useAnyway?: boolean;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const STORAGE_KEY = 'tc:triage:v2';
const SAVE_DEBOUNCE_MS = 800;

interface StoredState {
  drafts: [string, TriageDraft][];
  activePeriodStart: string | null;
  activePeriodEnd: string | null;
}

function loadFromStorage(): { drafts: Map<string, TriageDraft>; periodStart: string | null; periodEnd: string | null } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { drafts: new Map(), periodStart: null, periodEnd: null };
    const stored = JSON.parse(raw) as StoredState;
    return {
      drafts: new Map(stored.drafts ?? []),
      periodStart: stored.activePeriodStart ?? null,
      periodEnd: stored.activePeriodEnd ?? null,
    };
  } catch {
    return { drafts: new Map(), periodStart: null, periodEnd: null };
  }
}

function saveToStorage(
  map: Map<string, TriageDraft>,
  ps: string | null,
  pe: string | null,
): void {
  try {
    const stored: StoredState = {
      drafts: [...map.entries()],
      activePeriodStart: ps,
      activePeriodEnd: pe,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // quota exceeded or private browsing — silently skip
  }
}

function fromWire(e: DraftEdit): TriageDraft {
  return { ...e, status: e.status as TriageStatus };
}

export const useTriageStore = defineStore('triage', () => {
  const { drafts: storedDrafts, periodStart: storedPs, periodEnd: storedPe } = loadFromStorage();

  const drafts = ref(storedDrafts);
  const activePeriodStart = ref<string | null>(storedPs);
  const activePeriodEnd = ref<string | null>(storedPe);
  const saveStatus = ref<SaveStatus>('idle');

  // The saved draft (ADR-0031) is the system of record per (tenant, user, period);
  // sessionStorage is an offline fallback so a flaky network never loses an edit
  // between server saves. `hydrating` guards the watcher so loading the server
  // overlay doesn't immediately echo back as a save.
  let hydrating = false;
  let saveTimer: ReturnType<typeof setTimeout> | null = null;

  function persistLocal(): void {
    saveToStorage(drafts.value, activePeriodStart.value, activePeriodEnd.value);
  }

  function scheduleServerSave(): void {
    const period = activePeriodStart.value;
    if (!period) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveStatus.value = 'saving';
    saveTimer = setTimeout(() => {
      void saveDraftEdits(period, [...drafts.value.values()])
        .then(() => { saveStatus.value = 'saved'; })
        .catch(() => { saveStatus.value = 'error'; });   // sessionStorage still holds the edit
    }, SAVE_DEBOUNCE_MS);
  }

  function onDraftsChanged(): void {
    persistLocal();              // offline cache, always
    if (hydrating) return;       // don't re-POST what we just loaded
    scheduleServerSave();
  }

  async function hydrateFromServer(period: string): Promise<void> {
    try {
      const entries = await getDraftEdits(period);
      hydrating = true;
      drafts.value = new Map(entries.map((e): [string, TriageDraft] => [e.eventId, fromWire(e)]));
      await nextTick();
    } catch {
      // offline / error — keep whatever sessionStorage gave us
    } finally {
      hydrating = false;
    }
  }

  watch(drafts, onDraftsChanged, { deep: true });
  watch([activePeriodStart, activePeriodEnd], persistLocal);
  watch(activePeriodStart, (period) => {
    if (period) void hydrateFromServer(period);
  }, { immediate: true });

  function getDraft(eventId: string): TriageDraft | undefined {
    return drafts.value.get(eventId);
  }

  // An entry is replaced, not merged, so anything a later write forgets is
  // lost. The "use anyway" override must not be: it says Vantagepoint does not
  // hold this work, which stays true through a confirm, a re-target, a dismiss
  // and an undo. Dropping it lets the server's written mark reassert and the
  // work vanish the moment it comes back (#6487). Carrying it here holds for
  // every writer rather than the ones that remembered to ask.
  function setDraft(draft: TriageDraft): void {
    const carried = draft.useAnyway === undefined && drafts.value.get(draft.eventId)?.useAnyway
      ? { ...draft, useAnyway: true }
      : draft;
    drafts.value.set(draft.eventId, carried);
  }

  // Drop all per-event edits (e.g. "Re-draft everything"), leaving the active
  // period intact. The server overlay is cleared by the redraft_all trigger.
  function clearDrafts(): void {
    drafts.value = new Map();
  }

  // Put the overlay back to a snapshot taken before a write (#6542). A whole-map
  // replace rather than a per-entry rollback: an entry a write *replaced* has no
  // inverse to apply, and there is no per-event delete to undo one it added. The
  // watcher persists and re-posts the restored map like any other change.
  //
  // Takes ownership of `snapshot` — it is already a copy, made at write time.
  function restore(snapshot: Map<string, TriageDraft>): void {
    drafts.value = snapshot;
  }

  function clear(): void {
    drafts.value = new Map();
    activePeriodStart.value = null;
    activePeriodEnd.value = null;
    sessionStorage.removeItem(STORAGE_KEY);
  }

  return {
    drafts,
    activePeriodStart,
    activePeriodEnd,
    saveStatus,
    getDraft,
    setDraft,
    clearDrafts,
    restore,
    clear,
    hydrateFromServer,
  };
});
